import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../../API/EndPoints";
import AxiosInstance from "../../API/AxiosInstance";

// GET ALL
export const fetchDoctors = createAsyncThunk(
  "doctor/fetchDoctors",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(API_ENDPOINTS.GET_DOCTORS);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// CREATE
export const createDoctor = createAsyncThunk(
  "doctor/createDoctor",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        API_ENDPOINTS.CREATE_DOCTOR,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// UPDATE
export const updateDoctor = createAsyncThunk(
  "doctor/updateDoctor",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.put(
        API_ENDPOINTS.UPDATE_DOCTOR,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// DELETE
export const deleteDoctor = createAsyncThunk(
  "doctor/deleteDoctor",
  async (doctorId, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.delete(
        `${API_ENDPOINTS.DELETE_DOCTOR}?doctorId=${doctorId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const uploadDoctorFile = createAsyncThunk(
  "doctor/upload",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await AxiosInstance.post(
        API_ENDPOINTS.UPLOAD_IMAGE,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data", // ✅ required for binary upload
          },
        }
      );
      return res.data; // backend should return { url: "...uploaded file url..." }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// CREATE
export const scheduleAppointment = createAsyncThunk(
  "doctor/scheduleAppointment",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        API_ENDPOINTS.SCHEDULE_APPOINTMENT,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchDoctorSchedule = createAsyncThunk(
  "doctor/fetchDoctorSchedule",
  async (doctorId, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${API_ENDPOINTS.GET_SCHEDULE}?doctorId=${doctorId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const updateSchedule = createAsyncThunk(
  "doctor/updateSchedule",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.put(
        API_ENDPOINTS.UPDATE_SCHEDULE,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

const DoctorSlice = createSlice({
  name: "doctor",
  initialState: {
    fetch: { data: [], loading: false, error: null },
    create: { data: null, loading: false, error: null },
    update: { data: null, loading: false, error: null },
    delete: { data: null, loading: false, error: null },
    upload: { data: [], loading: false, error: null },
    schedule: { data: null, loading: false, error: null },
    updateSchdule: { data: null, loading: false, error: null },
    fetchSchedule: { data: [], loading: false, error: null },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchDoctors.pending, (state) => {
        state.fetch.loading = true;
        state.fetch.error = null;
      })
      .addCase(fetchDoctors.fulfilled, (state, action) => {
        state.fetch.loading = false;
        state.fetch.data = action.payload || [];
      })
      .addCase(fetchDoctors.rejected, (state, action) => {
        state.fetch.loading = false;
        state.fetch.error = action.payload;
      })

      // CREATE
      .addCase(createDoctor.pending, (state) => {
        state.create.loading = true;
        state.create.error = null;
      })
      .addCase(createDoctor.fulfilled, (state, action) => {
        state.create.loading = false;
        state.create.data = action.payload;
      })
      .addCase(createDoctor.rejected, (state, action) => {
        state.create.loading = false;
        state.create.error = action.payload;
      })

      // UPDATE
      .addCase(updateDoctor.pending, (state) => {
        state.update.loading = true;
        state.update.error = null;
      })
      .addCase(updateDoctor.fulfilled, (state, action) => {
        state.update.loading = false;
        state.update.data = action.payload;
      })
      .addCase(updateDoctor.rejected, (state, action) => {
        state.update.loading = false;
        state.update.error = action.payload;
      })

      // DELETE
      .addCase(deleteDoctor.pending, (state) => {
        state.delete.loading = true;
        state.delete.error = null;
      })
      .addCase(deleteDoctor.fulfilled, (state, action) => {
        state.delete.loading = false;
        state.delete.data = action.payload;
      })
      .addCase(deleteDoctor.rejected, (state, action) => {
        state.delete.loading = false;
        state.delete.error = action.payload;
      })

      .addCase(scheduleAppointment.pending, (state) => {
        state.schedule.loading = true;
        state.schedule.error = null;
      })
      .addCase(scheduleAppointment.fulfilled, (state, action) => {
        state.schedule.loading = false;
        state.schedule.data = action.payload || [];
      })
      .addCase(scheduleAppointment.rejected, (state, action) => {
        state.schedule.loading = false;
        state.schedule.error = action.payload;
      })

      // SEARCH
      .addCase(uploadDoctorFile.pending, (state) => {
        state.upload.loading = true;
        state.upload.error = null;
      })
      .addCase(uploadDoctorFile.fulfilled, (state, action) => {
        state.upload.loading = false;
        state.upload.data = action.payload || [];
      })
      .addCase(uploadDoctorFile.rejected, (state, action) => {
        state.upload.loading = false;
        state.upload.error = action.payload;
      })

      .addCase(fetchDoctorSchedule.pending, (state) => {
        state.fetchSchedule.loading = true;
        state.fetchSchedule.error = null;
      })
      .addCase(fetchDoctorSchedule.fulfilled, (state, action) => {
        state.fetchSchedule.loading = false;
        state.fetchSchedule.data = action.payload || [];
      })
      .addCase(fetchDoctorSchedule.rejected, (state, action) => {
        state.fetchSchedule.loading = false;
        state.fetchSchedule.error = action.payload;
      })
      .addCase(updateSchedule.pending, (state) => {
        state.updateSchdule.loading = true;
        state.updateSchdule.error = null;
      })
      .addCase(updateSchedule.fulfilled, (state, action) => {
        state.updateSchdule.loading = false;
        state.updateSchdule.data = action.payload || [];
      })
      .addCase(updateSchedule.rejected, (state, action) => {
        state.updateSchdule.loading = false;
        state.updateSchdule.error = action.payload;
      });
  },
});

export default DoctorSlice.reducer;
